package uk.co.nickthecoder.kyd

import uk.co.nickthecoder.kyd.appearance.AppearanceResource
import uk.co.nickthecoder.kyd.appearance.NoAppearance
import uk.co.nickthecoder.kyd.util.Vector2

class Role(

    parent: ParentResource,
    name: String

) : AbstractParentResource(parent, name), ResolvableResource, AppearanceResource {

    override val resourceType: ResourceType
        get() = ResourceType.ROLE

    var defaultAppearancePath: String = ""
        set(v) {
            if (field != v) {
                field = v
                defaultAppearance = find(v) as? AppearanceResource
            }
        }

    var defaultAppearance: AppearanceResource? = null
        private set


    /**
     * The name of the stage this Role is usually placed on.
     * When picking and placing, it will place it on the appropriate stage (if found).
     *
     * If this is left blank, then the new actor will be placed on the `current` stage.
     */
    var defaultStageName: String = ""

    var defaultAngleDegrees = 0f

    var defaultScale = Vector2.ONE

    var defaultZOrder = 0f

    /**
     * The qualified class name of the behaviour.
     *
     */
    var behaviourType: String = NoBehaviourClassName

    /**
     * Arbitrary attributes, specific to your game.
     *
     * For example, imagine a game where you control various spaceships.
     * All spaceships are controlled by a `Spaceship` [Behaviour], but there are different _types_,
     * some big, some small, and we need to set the maximum speed for each _type_.
     *
     * Create image(s) for a big spaceship, and a [Role] (called `BigSpaceship`) and add the max-speed
     * in these attributes.
     * Do the same for `SmallSpaceship` etc.
     *
     * Note. Role [attributes] work at a different level than Behaviour's `@Attribute` annotations.
     * These attributes should be used for each spaceship _type_, whereas `@Attribute` should be used for
     * a _specific_ spaceship.
     */
    val attributes = mutableMapOf<String, AttributeValue>()


    override fun resolveReferences(): String? {
        defaultAppearance = null
        if (defaultAppearancePath.isEmpty()) return null

        val resource = find(defaultAppearancePath) as? AppearanceResource
        if (resource == null) {
            return "Role ${path()} : Failed to find initial appearance : $defaultAppearancePath"
        } else {
            defaultAppearance = resource
            return null
        }
    }

    fun createActor(parent: ActorParent): Actor {
        return Actor(parent).apply {
            role = this@Role
            appearance = appearanceForActor(this)
            behaviour = createBehaviour()
            scale = defaultScale
            angleDegrees = defaultAngleDegrees
            zOrder = defaultZOrder
        }
    }

    override fun appearanceForActor(actor: Actor) = defaultAppearance?.appearanceForActor(actor) ?: NoAppearance

    override fun appearance() = defaultAppearance?.appearance() ?: NoAppearance

    fun createBehaviour() = KydTypeRegistry.createBehaviour(behaviourType)

    override fun toString() = "Role ${path()} appearance:'$defaultAppearancePath' (${defaultAppearance}) behaviour:'$behaviourType'"

}
